'use client';

import React, { useState, useRef, useEffect } from 'react';
import SubDropdown from './SubDropdown';
import type { NavbarLink, SubDrawer } from '../navbarData';

type Props = {
  link: NavbarLink;
  /** The merged (live + static) sub-drawer map passed down from Navbar. */
  subDrawers: Record<string, SubDrawer>;
};

export default function Dropdown({ link, subDrawers }: Props) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { label, href, subDrawerId } = link;

  // Resolve the drawer from the map passed in from the parent (Navbar).
  // The parent already merged live API data with the static fallback.
  const drawer = subDrawerId ? (subDrawers[subDrawerId] ?? null) : null;

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div
      className="relative inline-block z-10001"
      ref={dropdownRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <a
        href={href}
        title={label}
        className={`navbar__menu-btn flex flex-row justify-center items-center px-4.5 py-3 gap-2.5 w-auto h-8.5 rounded-2xl font-lato font-bold text-[14px] leading-3.5 tracking-[0.02em] text-[#1b365e] no-underline flex-none flex-grow-0 z-[7] transition-all duration-150 lg:px-[12px] lg:py-[8px] lg:text-[11px] md:px-[8px] md:py-[6px] md:text-[10px] ${
          open
            ? 'bg-[#c7f8ff] font-bold rounded-t-[20px] rounded-b-none relative z-100001 shadow-[0_2px_8px_rgba(27,54,94,0.08)] before:content-[""] before:absolute before:-top-5 before:left-0 before:right-0 before:bg-[#c7f8ff] after:content-[""] after:absolute after:-bottom-4.5 after:left-0 after:right-0 after:h-5 after:bg-[#c7f8ff]'
            : 'bg-white hover:bg-[#c7f8ff] hover:rounded-3xl active:bg-[#d9d9d9] active:border active:border-[#40bfde] active:rounded-3xl'
        }`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
      </a>

      {drawer && open && (
        <div className="navbar__dropdown-menu absolute top-full left-[-15px] p-[14px] pl-0 z-[100010]">
          <div className="navbar__dropdown-list-wrapper block">
            <div className="navbar__dropdown-inner flex gap-0">
              <SubDropdown drawer={drawer} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
